import { useCallback, useEffect, useState } from 'react';
import {
  CrystalTripResultv4,
  CrystalWithResultDTO,
  SynchTripDTO,
  SynchTripsExpReportDTO,
} from 'business/models/Models';
import SynchTripService from 'business/services/SynchrotronTripService';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  setAllCrystals,
  setEditedSynchTripData,
  setSelectedCrystalId,
  setSynchTripData,
  setTripEditStatus,
} from 'redux/reducers/synchrotronTripReducer';
import { showAlertSnackBar } from 'utils/Global';
import {
  selectCrystalId,
  selectSelectedAssignedCrystalIds,
  selectTripResultIDs,
} from 'redux/selectors/StateSelectors';
import { CrystalDataDTO, FetchStatus, SynchTripDataDTO } from './constants';
import { SynchTripReportOptionsProps } from 'components/synchrotron/constants';
import SynchrotronTripReport from 'components/reports/SynchrotronTripReport';
import {
  getPlainSynchTripData,
  getSynchTripDtoFromPlainData,
} from 'transformers/synchrotronTrip';
import { cloneDeep } from 'lodash';
import CrystalService from 'business/services/CrystalService';
import {
  useEditedSynchTrip,
  useIsCrystalAssignmentsDirty,
  useOriginalSynchTrip,
  useTripEditStatus,
} from 'redux/hooks/synchrotron';
import { CrystalTripResultStatus } from 'components/crystal/constants';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { TreeService } from 'business/services/TreeService';
import { useFileNotFoundModal } from 'CustomHooks/useFileNotFoundModal';
const synchTripService = new SynchTripService();
const crystalService = new CrystalService();

const useSynchTrip = (synchTripId: number) => {
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>();
  const [synchTripDto, setSynchTripDto] = useState<SynchTripDataDTO>();
  const dispatch = useRMDispatch();
  const showFileNotFoundModal = useFileNotFoundModal();

  const refetchSynchTrip = () => {
    setFetchStatus((prev: FetchStatus) => ({
      ...prev,
      refetch: !prev.refetch,
    }));
  };

  useEffect(() => {
    if (synchTripId) {
      const fetchData = async () => {
        setFetchStatus((prev: FetchStatus) => ({
          ...prev,
          isLoading: true,
        }));
        synchTripService
          .getSynchTripById(synchTripId)
          .then((data) => {
            const synchTripData = getPlainSynchTripData(data);
            setSynchTripDto(synchTripData);
            dispatch(setSynchTripData(JSON.parse(JSON.stringify(synchTripData))));
            setFetchStatus((prev: FetchStatus) => ({
              ...prev,
              isLoading: false,
            }));
          })
          .catch((error) => {
            if (error.status === 404) {
              showFileNotFoundModal();
            } else {
              throw error;
            }
          });
      };
      fetchData();
    }
  }, [synchTripId, fetchStatus?.refetch]);

  return { synchTripDto, refetchSynchTrip, fetchStatus };
};

const useSynchTripEdit = (
  unassignedCrystalsDto: CrystalDataDTO[],
  crystalsResultsDto: CrystalWithResultDTO[],
) => {
  const [crystalsResultsEdit, setCrystalsResultsEdit] =
    useState<CrystalWithResultDTO[]>();
  const [unassignedCrystalsEdit, setUnassignedCrystalsEdit] =
    useState<CrystalDataDTO[]>();
  const dispatch = useRMDispatch();
  const editedSynchTrip = useEditedSynchTrip();
  const originalSynchTrip = useOriginalSynchTrip();

  const handleInputChange = (e: any) => {
    dispatch(
      setEditedSynchTripData({
        ...editedSynchTrip,
        [e.target.name]: e.target.value,
        editStatus: { ...editedSynchTrip?.editStatus, isDirty: true },
      }),
    );
    dispatch(setTripEditStatus({ isDirty: true }));
  };

  const handleResultInputChange = (e: any, id: number) => {
    const { value, type } = e.target;
    const crystal = crystalsResultsEdit.find((c) => c.id === id);
    if (!crystal) {
      return;
    }
    let newValue = value;
    dispatch(setTripEditStatus({ isDirty: true }));
    if (type === 'radio') {
      newValue = parseFloat(newValue);
    }
    if (!crystal.result) {
      crystal.result = new CrystalTripResultv4();
      crystal.result.resultStatus = CrystalTripResultStatus.Partial;
    }
    crystal.result.crystalTripId = crystal.crystalTripId;
    crystal.result[e.target.name] = newValue;
    setCrystalsResultsEdit([...crystalsResultsEdit]);
  };

  const handleCollected = (crystalId: number) => {
    const updatedCrystals = editedSynchTrip?.assignedCrystals.map((c) => {
      if (c.id === crystalId) {
        return { ...c, dataCollected: !c.dataCollected };
      }
      return c;
    });
    dispatch(
      setEditedSynchTripData({
        ...editedSynchTrip,
        assignedCrystals: updatedCrystals,
      }),
    );
    dispatch(setTripEditStatus({ isDirty: true }));
  };

  const handleUnassignCrystal = (crystalIds: number[]) => {
    const crystals = editedSynchTrip?.assignedCrystals.filter(
      (c) => !crystalIds.includes(c.id),
    );
    const unassignedCrystals = editedSynchTrip?.assignedCrystals.filter((c) =>
      crystalIds.includes(c.id),
    );
    dispatch(
      setEditedSynchTripData({
        ...editedSynchTrip,
        assignedCrystals: [...crystals],
      }),
    );
    setUnassignedCrystalsEdit((prev: any) => [...prev, ...unassignedCrystals]);
    dispatch(setTripEditStatus({ isDirty: true }));
    dispatch(setSelectedCrystalId(null));
  };

  const handleAssignCrystals = (crystalIds: number[]) => {
    const crystals = unassignedCrystalsEdit?.filter((c) => crystalIds.includes(c.id));
    const remainingCrystals = unassignedCrystalsEdit?.filter(
      (c) => !crystalIds.includes(c.id),
    );
    setUnassignedCrystalsEdit(remainingCrystals);
    dispatch(
      setEditedSynchTripData({
        ...editedSynchTrip,
        assignedCrystals: [...editedSynchTrip.assignedCrystals, ...crystals],
      }),
    );
    dispatch(setTripEditStatus({ isDirty: true }));
    dispatch(setSelectedCrystalId(0));
  };

  const handleSave = () => {
    if (editedSynchTrip?.synchTripName.trim().length <= 0) {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'Crystal name cannot be empty.',
      );
      return;
    }
    dispatch(
      setEditedSynchTripData({
        ...editedSynchTrip,
        dataCollectionDate: new Date(editedSynchTrip.dataCollectionDate),
      }),
    );
    dispatch(setTripEditStatus({ isSaving: true }));
  };

  const handleDiscard = () => {
    dispatch(setEditedSynchTripData(originalSynchTrip));
    setUnassignedCrystalsEdit(unassignedCrystalsDto);
    setCrystalsResultsEdit(cloneDeep(crystalsResultsDto));
    dispatch(setTripEditStatus({ isDirty: false }));
  };

  useEffect(() => {
    if (unassignedCrystalsDto) {
      setUnassignedCrystalsEdit(unassignedCrystalsDto);
    }
  }, [unassignedCrystalsDto]);

  useEffect(() => {
    if (crystalsResultsDto) {
      setCrystalsResultsEdit(cloneDeep(crystalsResultsDto));
    }
  }, [crystalsResultsDto]);

  return {
    crystalsResultsEdit,
    unassignedCrystalsEdit,
    handleInputChange,
    handleResultInputChange,
    handleSave,
    handleDiscard,
    handleCollected,
    handleUnassignCrystal,
    handleAssignCrystals,
  };
};

const useSynchTripSave = (
  crystalsWithResults: CrystalWithResultDTO[],
  refetchSynchTrip: () => void,
  refetchUnassignedCrystals: () => void,
  refetchCollectedCrystals: () => void,
) => {
  const EditedSynchTrip = useEditedSynchTrip();
  const editStatus = useTripEditStatus();
  const dispatch = useRMDispatch();
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const isCrystalAssignmentsDirty = useIsCrystalAssignmentsDirty();
  const showFileNotFoundModal = useFileNotFoundModal();
  useEffect(() => {
    if (editStatus?.isSaving) {
      if (!isCrystalAssignmentsDirty) {
        const synchTripDto = getSynchTripDtoFromPlainData(EditedSynchTrip);
        synchTripService
          .saveSynchTrip(synchTripDto)
          .then(async () => {
            const treeService: TreeService = new TreeService();
            const treeNode = await treeService.getTreeNodeById(synchTripDto.treeNodeId);
            explorerBroadcastChannel.postMessage({
              action: CHANNEL_ACTIONS.EXPLORER_RENAME_NODE,
              payload: treeNode,
            });

            crystalService.updateCrystalResult(crystalsWithResults).then(() => {
              dispatch(setTripEditStatus({ isSaving: false, isDirty: false }));
              showAlertSnackBar(
                EAlertType.SUCCESS,
                ESnackBarPosition.TOP,
                'Synchrotron Trip successfully updated.',
              );
              refetchSynchTrip();
              refetchUnassignedCrystals();
              refetchCollectedCrystals();
              const newCrystals = EditedSynchTrip.assignedCrystals?.map((crystal) => ({
                ...crystal,
                isAssignmentsUpdated: false,
              }));
              setAllCrystals(newCrystals);
            });
          })
          .catch((ex) => {
            if (ex.StatusCode === 404) {
              showFileNotFoundModal();
            } else {
              showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, ex.ErrorMessage);
            }
          });
        dispatch(setTripEditStatus({ isSaving: false, isDirty: false }));
      } else {
        showAlertSnackBar(
          EAlertType.WARNING,
          ESnackBarPosition.TOP,
          'Please press apply to accept current crystal assignment changes or press cancel to reject it.',
        );
        dispatch(setTripEditStatus({ isSaving: false }));
      }
    }
  }, [editStatus]);
};

const useSelectedCrystalId = () => {
  const selectedCrystalId = useRMSelector(selectCrystalId);
  return [selectedCrystalId];
};

const useSelectedTripResultIDs = () => {
  const selectedTripIds = useRMSelector(selectTripResultIDs);
  return selectedTripIds;
};

const useSelectedAssignedCrystalIDs = () => {
  return useRMSelector(selectSelectedAssignedCrystalIds);
};

const useSynchTripReport = (synchTripId: number) => {
  const [reportOptions, setReportOptions] = useState<SynchTripReportOptionsProps>({
    tripInfoReport: true,
    tripResultReport: false,
  });
  const selectedAssignedCrystalIds = useRMSelector(selectSelectedAssignedCrystalIds);

  const openSynchTripReport = useCallback(
    async (ids: number[]) => {
      await synchTripService.getSynchTripReport(ids).then((data) => {
        SynchrotronTripReport(data, reportOptions, false, selectedAssignedCrystalIds);
      });
    },
    [reportOptions],
  );

  const saveSynchTripReport = useCallback(
    async (ids: number[]) => {
      await synchTripService.getSynchTripReport(ids).then((data) => {
        SynchrotronTripReport(data, reportOptions, true, selectedAssignedCrystalIds);
      });
    },
    [reportOptions],
  );

  const toggleSynchTripInfoReport = () => {
    setReportOptions({
      ...reportOptions,
      tripInfoReport: !reportOptions.tripInfoReport,
    });
  };

  const toggleSynchTripResultReport = () => {
    setReportOptions({
      ...reportOptions,
      tripResultReport: !reportOptions.tripResultReport,
    });
  };

  return {
    reportOptions,
    openSynchTripReport,
    saveSynchTripReport,
    toggleSynchTripInfoReport,
    toggleSynchTripResultReport,
  };
};

const useFetchSynchTripsByExpId = (expId: number) => {
  const [synchTrips, setSynchTrips] = useState<SynchTripDTO[]>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (expId) {
      const fetchData = async () => {
        setLoading(true);
        synchTripService.getSynchTripsByExpId(expId).then((data) => {
          setSynchTrips(data);
          setLoading(false);
        });
      };
      fetchData();
    }
  }, []);

  return { synchTrips, loading };
};

const useSynchTripsExpReport = () => {
  const reportOptions: SynchTripReportOptionsProps = {
    tripInfoReport: true,
    tripResultReport: true,
  };
  const openSynchTripsExpReport = useCallback(
    async (reportDto: SynchTripsExpReportDTO) => {
      await synchTripService.getSynchTripsExpReport(reportDto).then((data) => {
        SynchrotronTripReport(data, reportOptions);
      });
    },
    [],
  );

  const saveSynchTripsExpReport = useCallback(
    async (reportDto: SynchTripsExpReportDTO) => {
      await synchTripService.getSynchTripsExpReport(reportDto).then((data) => {
        SynchrotronTripReport(data, reportOptions, true);
      });
    },
    [reportOptions],
  );

  return { openSynchTripsExpReport, saveSynchTripsExpReport };
};

export {
  useSynchTrip,
  useSynchTripEdit,
  useSelectedCrystalId,
  useSelectedTripResultIDs,
  useSelectedAssignedCrystalIDs,
  useSynchTripSave,
  useSynchTripReport,
  useFetchSynchTripsByExpId,
  useSynchTripsExpReport,
};
